import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';

import { areEqual } from 'Utils/equalityChecks';
import { Api } from 'Utils/api';
import { useGHL } from 'Context/GHL';

import { ReferralChart } from './ReferralChart';
import { ReferralFilters } from './ReferralFilters';
import { ReferralDetailTable } from './ReferralDetailTable';
import {
  ReferralOpportunity,
  CompanyGroup,
  FilterOption,
  ReferralFilters as ReferralFiltersType,
  GhlContactApiResponse,
  GhlOpportunityApiResponse,
} from './types';

import classes from './referralDashboard.module.css';

const UNKNOWN_CONTACT_ID = '__unknown_contact__';

const buildContactName = (contact?: GhlContactApiResponse | null, fallback = 'Unattributed'): string => {
  if (!contact) return fallback;
  const displayName = contact.display_name?.trim();
  if (displayName) return displayName;
  const parts = [contact.first_name, contact.last_name].filter(Boolean);
  if (parts.length > 0) return parts.join(' ');
  return contact.company_name?.trim() || fallback;
};

const buildCompanyName = (contact?: GhlContactApiResponse | null): string => {
  if (!contact) return 'Unattributed';
  return contact.business?.name?.trim() || contact.display_name?.trim() || buildContactName(contact);
};

const buildOpportunity = (opportunity: GhlOpportunityApiResponse): ReferralOpportunity => {
  const leadContact = opportunity.contact ?? null;
  // Prefer referral_fee.referral_partner (set by backend), then fall back to referred_by
  const referralPartner = opportunity.referral_fee?.referral_partner
    ?? opportunity.referred_by
    ?? leadContact?.referred_by
    ?? null;
  const sourceLabel = opportunity.source?.trim() || 'Unattributed';
  const createdAt = opportunity.date_added || opportunity.created_at || '';
  const createdAtMs = createdAt ? Date.parse(createdAt) : 0;

  return {
    id: opportunity.id,
    opportunityName: opportunity.name || 'Opportunity',
    value: Number(opportunity.monetary_value ?? 0),
    createdAt,
    createdAtMs: Number.isNaN(createdAtMs) ? 0 : createdAtMs,
    status: (opportunity.status || 'open').toUpperCase(),
    referringCompany: referralPartner ? buildCompanyName(referralPartner) : sourceLabel,
    referringCompanyId: referralPartner?.business_id || referralPartner?.id || null,
    referringContactId: referralPartner?.id || UNKNOWN_CONTACT_ID,
    referringContactName: referralPartner ? buildContactName(referralPartner) : sourceLabel,
    leadContactId: leadContact?.id || '',
    leadContactName: buildContactName(leadContact, 'Unknown lead'),
    leadEmail: leadContact?.email ?? null,
    leadPhone: leadContact?.phone ?? null,
  };
};

const formatDateInput = (date: Date): string => date.toISOString().split('T')[0];

const buildInitialFilters = (): ReferralFiltersType => {
  const end = new Date();
  const start = new Date();
  start.setDate(start.getDate() - 90);
  return {
    selectedCompany: '',
    selectedContactId: '',
    startDate: formatDateInput(start),
    endDate: formatDateInput(end),
  };
};

function ReferralDashboardContainer() {
  const { isAuthenticated, locationId, ssoToken, error: ghlError, isLoading: ghlLoading } = useGHL();

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [opportunities, setOpportunities] = useState<ReferralOpportunity[]>([]);
  const [initialFilters] = useState(buildInitialFilters);
  const [filters, setFilters] = useState<ReferralFiltersType>(buildInitialFilters);
  const [detailCompany, setDetailCompany] = useState<string | null>(null);

  // Load data when location changes
  const loadData = useCallback(async () => {
    if (!locationId || !ssoToken) return;

    setIsLoading(true);
    setError(null);

    try {
      const response = await Api.get('/ghl/embed/opportunities', {
        params: {
          // Note: contact.referredBy is loaded automatically by backend via hardcoded with()
          include: 'contact,customFieldValues',
          limit: 500,
        },
        headers: {
          'X-GHL-SSO': ssoToken,
        },
      });

      const items = Array.isArray(response?.data?.data)
        ? response.data.data
        : Array.isArray(response?.data)
          ? response.data
          : [];

      setOpportunities(items.map(buildOpportunity));
    } catch (loadError) {
      // eslint-disable-next-line no-console
      console.error('Failed to load referral dashboard data', loadError);
      setError('Unable to load referral dashboard data. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }, [locationId, ssoToken]);

  useEffect(() => {
    if (isAuthenticated && locationId && ssoToken) {
      loadData();
    }
  }, [isAuthenticated, locationId, ssoToken, loadData]);

  // Build company filter options
  const companyOptions = useMemo((): FilterOption[] => {
    const companyMap = new Map<string, string>();

    opportunities.forEach((opp) => {
      if (opp.referringCompanyId) {
        companyMap.set(opp.referringCompanyId, opp.referringCompany);
      } else if (opp.referringCompany && opp.referringCompany !== 'Unattributed') {
        companyMap.set(opp.referringCompany, opp.referringCompany);
      }
    });

    return Array.from(companyMap.entries())
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [opportunities]);

  // Build contact filter options (filtered by selected company)
  const contactOptions = useMemo((): FilterOption[] => {
    const contacts = new Map<string, string>();

    opportunities.forEach((opp) => {
      // Filter by selected company if one is selected
      if (filters.selectedCompany) {
        const matchesCompanyId = opp.referringCompanyId === filters.selectedCompany;
        const matchesCompanyName = opp.referringCompany === filters.selectedCompany;
        if (!matchesCompanyId && !matchesCompanyName) return;
      }

      const key =
        opp.referringContactId !== UNKNOWN_CONTACT_ID
          ? opp.referringContactId
          : opp.referringContactName;
      contacts.set(key, opp.referringContactName);
    });

    return Array.from(contacts.entries())
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [opportunities, filters.selectedCompany]);

  // Filter opportunities
  const filteredOpportunities = useMemo(() => {
    const startMs = filters.startDate ? Date.parse(filters.startDate) : null;
    const endMs = filters.endDate ? Date.parse(filters.endDate) : null;

    return opportunities.filter((opp) => {
      // Filter by company
      if (filters.selectedCompany) {
        const matchesCompanyId = opp.referringCompanyId === filters.selectedCompany;
        const matchesCompanyName = opp.referringCompany === filters.selectedCompany;
        if (!matchesCompanyId && !matchesCompanyName) return false;
      }

      // Filter by contact
      if (filters.selectedContactId) {
        const matchesById = filters.selectedContactId === opp.referringContactId;
        const matchesByName = filters.selectedContactId === opp.referringContactName;
        if (!matchesById && !matchesByName) return false;
      }

      // Filter by date range
      if (startMs && opp.createdAtMs < startMs) return false;
      if (endMs && opp.createdAtMs > endMs + 86400000 - 1) return false;

      return true;
    });
  }, [opportunities, filters]);

  // Group opportunities by company
  const groupedByCompany = useMemo((): CompanyGroup[] => {
    const totals = new Map<string, CompanyGroup>();

    filteredOpportunities.forEach((opp) => {
      const key = opp.referringCompany || 'Unattributed';
      const existing = totals.get(key);

      if (existing) {
        existing.value += opp.value;
        existing.count += 1;
        existing.opportunities.push(opp);
      } else {
        totals.set(key, {
          company: key,
          value: opp.value,
          count: 1,
          opportunities: [opp],
        });
      }
    });

    return Array.from(totals.values()).sort((a, b) => b.value - a.value);
  }, [filteredOpportunities]);

  // Handlers
  const handleFilterChange = useCallback((key: keyof ReferralFiltersType, value: string) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  }, []);

  const handleResetFilters = useCallback(() => {
    setFilters(buildInitialFilters());
    setDetailCompany(null);
  }, []);

  const handleBarClick = useCallback((company: string) => {
    setDetailCompany((prev) => (prev === company ? null : company || null));
  }, []);

  // Clear detail when filtered data no longer contains the selected company
  useEffect(() => {
    if (detailCompany && !groupedByCompany.some((group) => group.company === detailCompany)) {
      setDetailCompany(null);
    }
  }, [groupedByCompany, detailCompany]);

  // Handle GHL loading/error states
  if (ghlLoading) {
    return (
      <div className={classes.container}>
        <div className={classes.loadingState}>
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p>Connecting to RocketCRM...</p>
        </div>
      </div>
    );
  }

  if (ghlError) {
    return (
      <div className={classes.container}>
        <div className={`alert alert-danger ${classes.errorCard}`}>
          <h4>Connection Error</h4>
          <p>{ghlError}</p>
          <p className={classes.helpText}>Please ensure this page is accessed from within RocketCRM.</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className={classes.container}>
        <div className={`alert alert-warning ${classes.warningCard}`}>
          <h4>Not Authenticated</h4>
          <p>Please access this page from within RocketCRM to view the referral dashboard.</p>
        </div>
      </div>
    );
  }

  if (!locationId) {
    return (
      <div className={classes.container}>
        <div className={`alert alert-warning ${classes.warningCard}`}>
          <h4>No Location Selected</h4>
          <p>Select a location in RocketCRM to view the referral dashboard.</p>
        </div>
      </div>
    );
  }

  const selectedGroup = detailCompany
    ? groupedByCompany.find((group) => group.company === detailCompany)
    : null;

  const allRecordsGroup = useMemo((): CompanyGroup | null => {
    if (filteredOpportunities.length === 0) return null;
    const totalValue = filteredOpportunities.reduce((sum, opp) => sum + opp.value, 0);
    return {
      company: 'All Referrals',
      value: totalValue,
      count: filteredOpportunities.length,
      opportunities: filteredOpportunities,
    };
  }, [filteredOpportunities]);

  const displayGroup = selectedGroup || allRecordsGroup;

  return (
    <div className={classes.container}>
      <ReferralFilters
        filters={filters}
        defaultFilters={initialFilters}
        companyOptions={companyOptions}
        contactOptions={contactOptions}
        onFilterChange={handleFilterChange}
        onReset={handleResetFilters}
      />

      <div className={classes.chartCard}>
        <div className={classes.chartHeader}>
          <h4 className={classes.chartTitle}>Job Value by Referring Company</h4>
          <p className={classes.chartSubtitle}>
            Total opportunity value grouped by referral partner company.
          </p>
        </div>

        <ReferralChart
          data={groupedByCompany}
          isLoading={isLoading}
          error={error}
          onBarClick={handleBarClick}
          selectedCompany={detailCompany}
        />

        {!isLoading && !error && groupedByCompany.length > 0 && (
          <div className={classes.chartBaseline} />
        )}
      </div>

      {displayGroup && (
        <ReferralDetailTable
          group={displayGroup}
          onClose={selectedGroup ? () => setDetailCompany(null) : undefined}
        />
      )}
    </div>
  );
}

const ReferralDashboardContainerMemo = memo(ReferralDashboardContainer, areEqual);

export { ReferralDashboardContainerMemo as ReferralDashboardContainer };
